/**
 * Expression Parser
 * Parses expressions into an AST for validation
 * Supports function calls, operators, variables, and literals
 */

// Token types
const TOKEN_TYPES = {
  NUMBER: 'NUMBER',
  STRING: 'STRING',
  IDENTIFIER: 'IDENTIFIER',
  OPERATOR: 'OPERATOR',
  LPAREN: 'LPAREN',
  RPAREN: 'RPAREN',
  // Array indexing — added so `nos[i]` / `nos[i][j]` parse natively
  // instead of emitting UNKNOWN tokens that the validator surfaces as
  // "Variable '[' is not declared".
  LBRACKET: 'LBRACKET',
  RBRACKET: 'RBRACKET',
  COMMA: 'COMMA',
  DOT: 'DOT',
  // ADO #354 — `->` pointer-member access (ptr->member). Tokenized as a
  // distinct type (not the `-` / `>` operators) so the parser treats it as
  // member access, mirroring DOT.
  ARROW: 'ARROW',
  EOF: 'EOF',
};

// AST Node types
export const AST_TYPES = {
  PROGRAM: 'Program',
  BINARY_EXPR: 'BinaryExpression',
  UNARY_EXPR: 'UnaryExpression',
  CALL_EXPR: 'CallExpression',
  MEMBER_EXPR: 'MemberExpression',
  IDENTIFIER: 'Identifier',
  LITERAL: 'Literal',
  // ADO #404 — ternary `cond ? a : b`. Distinct node so identifier/operator
  // extraction walks all three branches (otherwise vars inside a/b surface as
  // "undeclared").
  CONDITIONAL_EXPR: 'ConditionalExpression',
  // ADO #404 — C cast `(int)x`. Distinct node so the type name is NOT treated
  // as a variable while the operand is still walked.
  CAST_EXPR: 'CastExpression',
  // ADO #408 — increment/decrement `p++`, `++p`, `i--`. Distinct node carrying
  // `prefix` (true for `++p`, false for `p++`); for validation it behaves like
  // a unary op — walk the argument, capture the operator.
  UPDATE_EXPR: 'UpdateExpression',
};

// ADO #404 — primitive/modifier type keywords that can head a C cast:
// `(int)x`, `(unsigned long)n`, `(float*)p`. struct/union/enum casts reuse the
// type-name handling already used for sizeof. Kept small on purpose — a token
// that isn't one of these can't open a cast, so `(a)*b` stays a multiplication.
const C_TYPE_KEYWORDS = new Set([
  'int', 'char', 'float', 'double', 'void', 'short', 'long', 'unsigned', 'signed', 'bool',
  'size_t', 'int8_t', 'int16_t', 'int32_t', 'int64_t',
  'uint8_t', 'uint16_t', 'uint32_t', 'uint64_t',
]);

/**
 * Tokenizer - converts expression string to tokens
 */
class Tokenizer {
  constructor(input) {
    this.input = input;
    this.pos = 0;
    this.tokens = [];
  }

  isWhitespace(char) {
    return /\s/.test(char);
  }

  isDigit(char) {
    return /[0-9]/.test(char);
  }

  isAlpha(char) {
    return /[a-zA-Z_]/.test(char);
  }

  isAlphaNum(char) {
    return /[a-zA-Z0-9_]/.test(char);
  }

  peek(offset = 0) {
    return this.input[this.pos + offset];
  }

  advance() {
    return this.input[this.pos++];
  }

  tokenize() {
    while (this.pos < this.input.length) {
      const char = this.peek();

      // Skip whitespace
      if (this.isWhitespace(char)) {
        this.advance();
        continue;
      }

      // Numbers
      if (this.isDigit(char) || (char === '.' && this.isDigit(this.peek(1)))) {
        this.readNumber();
        continue;
      }

      // Strings
      if (char === '"' || char === "'") {
        this.readString(char);
        continue;
      }

      // Identifiers (variables, keywords, function names)
      if (this.isAlpha(char)) {
        this.readIdentifier();
        continue;
      }

      // Operators and punctuation
      if (this.readOperator()) {
        continue;
      }

      // Unknown character - include it as an error token
      this.tokens.push({ type: 'UNKNOWN', value: char, pos: this.pos });
      this.advance();
    }

    this.tokens.push({ type: TOKEN_TYPES.EOF, value: '', pos: this.pos });
    return this.tokens;
  }

  readNumber() {
    const start = this.pos;
    let value = '';
    let hasDot = false;

    // ADO #404 — hex (0x..) / binary (0b..) literals. Read the prefix + the
    // run of hex/binary digits as ONE token so `0xFF` tokenizes as a number
    // instead of `0` followed by an identifier `xFF` (which misparses and
    // surfaces a false "undeclared variable"). Octal (`077`) needs no special
    // case — the decimal loop below reads its digits and the raw is preserved.
    if (this.peek() === '0' && /[xXbB]/.test(this.peek(1) || '')) {
      value += this.advance(); // 0
      value += this.advance(); // x / b
      while (this.pos < this.input.length && /[0-9a-fA-F]/.test(this.peek())) {
        value += this.advance();
      }
      this.tokens.push({ type: TOKEN_TYPES.NUMBER, value, pos: start });
      return;
    }

    while (this.pos < this.input.length) {
      const char = this.peek();
      if (this.isDigit(char)) {
        value += this.advance();
      } else if (char === '.' && !hasDot && this.isDigit(this.peek(1))) {
        hasDot = true;
        value += this.advance();
      } else {
        break;
      }
    }
    // ADO #579 — C's suffixes belong to the number: 2147483648L, 10UL, 1.5f.
    const suffix = this.input.slice(this.pos).match(/^(?:[uU](?:ll|LL|l|L)?|(?:ll|LL|l|L)[uU]?|[fF])(?![A-Za-z0-9_])/);
    if (suffix) { value += suffix[0]; this.pos += suffix[0].length; }

    this.tokens.push({ type: TOKEN_TYPES.NUMBER, value, pos: start });
  }

  readString(quote) {
    const start = this.pos;
    this.advance(); // consume opening quote
    let value = '';

    while (this.pos < this.input.length && this.peek() !== quote) {
      if (this.peek() === '\\') {
        this.advance();
        if (this.pos < this.input.length) {
          value += this.advance();
        }
      } else {
        value += this.advance();
      }
    }

    if (this.peek() === quote) {
      this.advance(); // consume closing quote
    }

    // ADO #297 — preserve the quote style so downstream validators can
    // tell `'foo'` (multi-char int constant in C) from `"foo"` (string
    // literal). Without this, the Action node can't catch students
    // typing single quotes for a `char*` argument and shipping a
    // segfault.
    this.tokens.push({ type: TOKEN_TYPES.STRING, value, quote, pos: start });
  }

  readIdentifier() {
    const start = this.pos;
    let value = '';

    while (this.pos < this.input.length && this.isAlphaNum(this.peek())) {
      value += this.advance();
    }

    this.tokens.push({ type: TOKEN_TYPES.IDENTIFIER, value, pos: start });
  }

  readOperator() {
    const char = this.peek();
    const start = this.pos;

    // Three-character operators
    const threeChar = this.input.slice(this.pos, this.pos + 3);
    if (['===', '!=='].includes(threeChar)) {
      this.pos += 3;
      this.tokens.push({ type: TOKEN_TYPES.OPERATOR, value: threeChar, pos: start });
      return true;
    }

    // ADO #354 — `->` pointer-member access. Checked before the single-char
    // `-` / `>` branch so it isn't split into two operator tokens.
    if (this.input.slice(this.pos, this.pos + 2) === '->') {
      this.pos += 2;
      this.tokens.push({ type: TOKEN_TYPES.ARROW, value: '->', pos: start });
      return true;
    }

    // Two-character operators.
    // ADO #404 — `<<` / `>>` (bitwise shift) added here so they tokenize as one
    // operator before the single-char `<` / `>` branch splits them.
    // ADO #408 — `++` / `--` (increment/decrement) added here so they tokenize
    // as a single operator before the single-char `+` / `-` branch splits them
    // into two tokens (which previously made `p++` a parse error).
    const twoChar = this.input.slice(this.pos, this.pos + 2);
    if (['==', '!=', '<=', '>=', '&&', '||', '**', '//', '+=', '-=', '*=', '/=', '%=', '<<', '>>', '++', '--'].includes(twoChar)) {
      this.pos += 2;
      this.tokens.push({ type: TOKEN_TYPES.OPERATOR, value: twoChar, pos: start });
      return true;
    }

    // Single-character operators and punctuation.
    // ADO #291 — `&` recognised so address-of (`&x`) parses; `*` was already
    // here for multiplication and now also serves as dereference (handled in
    // parseUnary's whitelist below). `&&` (logical AND) is captured by the
    // two-char branch above so the standalone `&` here is unambiguous.
    // ADO #404 — `| ^ ~` (bitwise OR/XOR/NOT) and `? :` (ternary) added.
    if ('+-*/%<>=!&|^~?:'.includes(char)) {
      this.advance();
      this.tokens.push({ type: TOKEN_TYPES.OPERATOR, value: char, pos: start });
      return true;
    }

    if (char === '(') {
      this.advance();
      this.tokens.push({ type: TOKEN_TYPES.LPAREN, value: char, pos: start });
      return true;
    }

    if (char === ')') {
      this.advance();
      this.tokens.push({ type: TOKEN_TYPES.RPAREN, value: char, pos: start });
      return true;
    }

    if (char === '[') {
      this.advance();
      this.tokens.push({ type: TOKEN_TYPES.LBRACKET, value: char, pos: start });
      return true;
    }

    if (char === ']') {
      this.advance();
      this.tokens.push({ type: TOKEN_TYPES.RBRACKET, value: char, pos: start });
      return true;
    }

    if (char === ',') {
      this.advance();
      this.tokens.push({ type: TOKEN_TYPES.COMMA, value: char, pos: start });
      return true;
    }

    if (char === '.') {
      this.advance();
      this.tokens.push({ type: TOKEN_TYPES.DOT, value: char, pos: start });
      return true;
    }

    return false;
  }
}

/**
 * Parser - converts tokens to AST
 */
class Parser {
  constructor(tokens) {
    this.tokens = tokens;
    this.pos = 0;
  }

  peek(offset = 0) {
    return this.tokens[this.pos + offset] || { type: TOKEN_TYPES.EOF };
  }

  advance() {
    return this.tokens[this.pos++];
  }

  expect(type) {
    const token = this.peek();
    if (token.type !== type) {
      throw new Error(`Expected ${type} but got ${token.type}`);
    }
    return this.advance();
  }

  parse() {
    const body = [];
    
    while (this.peek().type !== TOKEN_TYPES.EOF) {
      const expr = this.parseExpression();
      if (expr) body.push(expr);
    }

    return {
      type: AST_TYPES.PROGRAM,
      body,
    };
  }

  parseExpression() {
    return this.parseTernary();
  }

  // ADO #404 — ternary sits above logical-OR and below assignment (C precedence
  // 15). Right-associative: the alternate recurses into parseTernary so
  // `a ? b : c ? d : e` nests as `a ? b : (c ? d : e)`.
  parseTernary() {
    const test = this.parseLogicalOr();

    if (this.peek().type === TOKEN_TYPES.OPERATOR && this.peek().value === '?') {
      this.advance(); // consume '?'
      const consequent = this.parseExpression();
      if (this.peek().type === TOKEN_TYPES.OPERATOR && this.peek().value === ':') {
        this.advance(); // consume ':'
      } else {
        throw new Error(`Expected ':' in ternary but got ${this.peek().type}`);
      }
      const alternate = this.parseTernary();
      return {
        type: AST_TYPES.CONDITIONAL_EXPR,
        test,
        consequent,
        alternate,
      };
    }

    return test;
  }

  parseLogicalOr() {
    let left = this.parseLogicalAnd();

    while (this.peek().type === TOKEN_TYPES.OPERATOR && 
           (this.peek().value === '||' || this.peek().value === 'or')) {
      const operator = this.advance().value;
      const right = this.parseLogicalAnd();
      left = {
        type: AST_TYPES.BINARY_EXPR,
        operator,
        left,
        right,
      };
    }

    return left;
  }

  parseLogicalAnd() {
    let left = this.parseBitwiseOr();

    while (this.peek().type === TOKEN_TYPES.OPERATOR &&
           (this.peek().value === '&&' || this.peek().value === 'and')) {
      const operator = this.advance().value;
      const right = this.parseBitwiseOr();
      left = {
        type: AST_TYPES.BINARY_EXPR,
        operator,
        left,
        right,
      };
    }

    return left;
  }

  // ADO #404 — bitwise OR / XOR / AND sit between logical-AND and equality, in
  // their own precedence tiers (C: | < ^ < & < ==). Binary `&` here is distinct
  // from prefix `&` (address-of) handled in parseUnary, and from `&&` which the
  // tokenizer captures as a single token consumed in parseLogicalAnd.
  parseBitwiseOr() {
    let left = this.parseBitwiseXor();
    while (this.peek().type === TOKEN_TYPES.OPERATOR && this.peek().value === '|') {
      const operator = this.advance().value;
      const right = this.parseBitwiseXor();
      left = { type: AST_TYPES.BINARY_EXPR, operator, left, right };
    }
    return left;
  }

  parseBitwiseXor() {
    let left = this.parseBitwiseAnd();
    while (this.peek().type === TOKEN_TYPES.OPERATOR && this.peek().value === '^') {
      const operator = this.advance().value;
      const right = this.parseBitwiseAnd();
      left = { type: AST_TYPES.BINARY_EXPR, operator, left, right };
    }
    return left;
  }

  parseBitwiseAnd() {
    let left = this.parseEquality();
    while (this.peek().type === TOKEN_TYPES.OPERATOR && this.peek().value === '&') {
      const operator = this.advance().value;
      const right = this.parseEquality();
      left = { type: AST_TYPES.BINARY_EXPR, operator, left, right };
    }
    return left;
  }

  parseEquality() {
    let left = this.parseComparison();

    while (this.peek().type === TOKEN_TYPES.OPERATOR && 
           ['==', '!=', '===', '!=='].includes(this.peek().value)) {
      const operator = this.advance().value;
      const right = this.parseComparison();
      left = {
        type: AST_TYPES.BINARY_EXPR,
        operator,
        left,
        right,
      };
    }

    return left;
  }

  parseComparison() {
    let left = this.parseShift();

    while (this.peek().type === TOKEN_TYPES.OPERATOR &&
           ['<', '>', '<=', '>=', 'in', 'is'].includes(this.peek().value)) {
      const operator = this.advance().value;
      const right = this.parseShift();
      left = {
        type: AST_TYPES.BINARY_EXPR,
        operator,
        left,
        right,
      };
    }

    return left;
  }

  // ADO #404 — bitwise shift sits between relational and additive (C: shift is
  // lower precedence than + - but higher than < >). So `1 << 2 + 3` parses as
  // `1 << (2 + 3)` and `a < b << c` as `a < (b << c)`.
  parseShift() {
    let left = this.parseAdditive();
    while (this.peek().type === TOKEN_TYPES.OPERATOR &&
           ['<<', '>>'].includes(this.peek().value)) {
      const operator = this.advance().value;
      const right = this.parseAdditive();
      left = { type: AST_TYPES.BINARY_EXPR, operator, left, right };
    }
    return left;
  }

  parseAdditive() {
    let left = this.parseMultiplicative();

    while (this.peek().type === TOKEN_TYPES.OPERATOR && 
           ['+', '-'].includes(this.peek().value)) {
      const operator = this.advance().value;
      const right = this.parseMultiplicative();
      left = {
        type: AST_TYPES.BINARY_EXPR,
        operator,
        left,
        right,
      };
    }

    return left;
  }

  parseMultiplicative() {
    let left = this.parsePower();

    while (this.peek().type === TOKEN_TYPES.OPERATOR && 
           ['*', '/', '%', '//'].includes(this.peek().value)) {
      const operator = this.advance().value;
      const right = this.parsePower();
      left = {
        type: AST_TYPES.BINARY_EXPR,
        operator,
        left,
        right,
      };
    }

    return left;
  }

  parsePower() {
    let left = this.parseUnary();

    while (this.peek().type === TOKEN_TYPES.OPERATOR && this.peek().value === '**') {
      const operator = this.advance().value;
      const right = this.parseUnary();
      left = {
        type: AST_TYPES.BINARY_EXPR,
        operator,
        left,
        right,
      };
    }

    return left;
  }

  parseUnary() {
    // ADO #404 — C cast `(type)operand` at unary precedence. Checked before the
    // unary-operator and primary branches so `(int)x` doesn't parse as a
    // parenthesized `int` (flagged undeclared) followed by a stray `x`.
    const cast = this.tryParseCast();
    if (cast) return cast;

    // ADO #408 — prefix increment/decrement (`++p`, `--p`). Checked at unary
    // precedence; the operand is parsed recursively so `++*p` / `++arr[i]` work.
    if (this.peek().type === TOKEN_TYPES.OPERATOR &&
        (this.peek().value === '++' || this.peek().value === '--')) {
      const operator = this.advance().value;
      const argument = this.parseUnary();
      return {
        type: AST_TYPES.UPDATE_EXPR,
        operator,
        argument,
        prefix: true,
      };
    }

    // ADO #579 — `sizeof x` / `sizeof cells[k]` without brackets: an operator on
    // a unary expression. (`sizeof(...)` keeps parsing as a call, which the
    // validator now accepts as the operator it is.)
    if (this.peek().type === TOKEN_TYPES.IDENTIFIER && this.peek().value === 'sizeof'
        && this.peek(1)?.type !== TOKEN_TYPES.LPAREN) {
      this.advance();
      return { type: AST_TYPES.UNARY_EXPR, operator: 'sizeof', argument: this.parseUnary() };
    }

    // ADO #291 — `*` (dereference) and `&` (address-of) join the existing
    // unary set so `*a`, `&x`, `*ptr + 1` parse as UnaryExpression rather
    // than triggering the validator's "Variable '*' is not declared" false
    // positive. extractIdentifiers already walks UnaryExpression.argument.
    if (this.peek().type === TOKEN_TYPES.OPERATOR &&
        ['!', '-', '+', '*', '&', '~', 'not'].includes(this.peek().value)) {
      const operator = this.advance().value;
      const argument = this.parseUnary();
      return {
        type: AST_TYPES.UNARY_EXPR,
        operator,
        argument,
      };
    }

    return this.parseCallOrMember();
  }

  // ADO #404 — recognise a leading `( type-name )` cast without consuming a
  // plain parenthesized expression. Pure lookahead first; only commits (advances)
  // once the closing `)` after the type tokens is confirmed.
  tryParseCast() {
    if (this.peek().type !== TOKEN_TYPES.LPAREN) return null;

    const tok = (k) => this.tokens[k] || { type: TOKEN_TYPES.EOF };
    let i = this.pos + 1;
    let sawType = false;

    if (['struct', 'union', 'enum'].includes(tok(i).value) &&
        tok(i + 1).type === TOKEN_TYPES.IDENTIFIER) {
      i += 2;
      sawType = true;
    } else {
      while (tok(i).type === TOKEN_TYPES.IDENTIFIER && C_TYPE_KEYWORDS.has(tok(i).value)) {
        i += 1;
        sawType = true;
      }
    }
    if (!sawType) return null;

    // Optional pointer stars: `(char*)`, `(void **)`.
    while (tok(i).type === TOKEN_TYPES.OPERATOR && tok(i).value === '*') i += 1;
    if (tok(i).type !== TOKEN_TYPES.RPAREN) return null;

    // Confirmed cast — consume `( type... )` and build the type-name string.
    this.advance(); // (
    let cType = '';
    while (this.peek().type !== TOKEN_TYPES.RPAREN) {
      const t = this.advance();
      cType += (cType && t.type === TOKEN_TYPES.IDENTIFIER ? ' ' : '') + t.value;
    }
    this.advance(); // )

    const argument = this.parseUnary();
    return {
      type: AST_TYPES.CAST_EXPR,
      cType,
      argument,
    };
  }

  parseCallOrMember() {
    let expr = this.parsePrimary();

    while (true) {
      if (this.peek().type === TOKEN_TYPES.LPAREN) {
        // Function call
        this.advance(); // consume '('
        const args = this.parseArguments();
        this.expect(TOKEN_TYPES.RPAREN);
        
        expr = {
          type: AST_TYPES.CALL_EXPR,
          callee: expr,
          arguments: args,
        };
      } else if (this.peek().type === TOKEN_TYPES.DOT ||
                 this.peek().type === TOKEN_TYPES.ARROW) {
        // Member access — `.` (value struct) or `->` (pointer-to-struct).
        // Both produce the same MemberExpression shape so validation /
        // identifier extraction treat them identically; `arrow` is kept for
        // any consumer that needs to distinguish the two.
        const arrow = this.advance().type === TOKEN_TYPES.ARROW; // consume '.' or '->'
        const property = this.expect(TOKEN_TYPES.IDENTIFIER);

        expr = {
          type: AST_TYPES.MEMBER_EXPR,
          object: expr,
          property: {
            type: AST_TYPES.IDENTIFIER,
            name: property.value,
          },
          arrow,
        };
      } else if (this.peek().type === TOKEN_TYPES.LBRACKET) {
        // Array index access — `nos[i]`, chained for 2D `nos[i][j]`.
        // Emits a MemberExpression with `computed: true` so the index
        // expression itself is walked by extractIdentifiers (and any
        // nested function call inside `[…]` is captured too).
        this.advance(); // consume '['
        const index = this.parseExpression();
        this.expect(TOKEN_TYPES.RBRACKET);

        expr = {
          type: AST_TYPES.MEMBER_EXPR,
          object: expr,
          property: index,
          computed: true,
        };
      } else {
        break;
      }
    }

    // ADO #408 — postfix increment/decrement (`p++`, `i--`, `arr[i]++`). Applied
    // after the call/member/subscript loop so it binds to the whole postfix
    // chain, matching C (`a.b++` increments `a.b`, not `a`).
    if (this.peek().type === TOKEN_TYPES.OPERATOR &&
        (this.peek().value === '++' || this.peek().value === '--')) {
      const operator = this.advance().value;
      expr = {
        type: AST_TYPES.UPDATE_EXPR,
        operator,
        argument: expr,
        prefix: false,
      };
    }

    return expr;
  }

  parseArguments() {
    const args = [];

    if (this.peek().type === TOKEN_TYPES.RPAREN) {
      return args;
    }

    args.push(this.parseArgument());

    while (this.peek().type === TOKEN_TYPES.COMMA) {
      this.advance(); // consume ','
      args.push(this.parseArgument());
    }

    return args;
  }

  // ADO #434 — accept a Python keyword argument `name=value` (e.g.
  // `nums.sort(reverse=True)`). The C-style parser would otherwise stop at the
  // bare `=` ("Expected RPAREN but got OPERATOR"). We keep only the VALUE for
  // identifier extraction — the keyword name is a parameter label, not a
  // variable reference, so it must not be flagged "undeclared".
  parseArgument() {
    const expr = this.parseExpression();
    const nxt = this.peek();
    if (nxt.type === TOKEN_TYPES.OPERATOR && nxt.value === '=') {
      this.advance(); // consume '='
      return this.parseExpression(); // the value expression replaces the kwarg
    }
    return expr;
  }

  parsePrimary() {
    const token = this.peek();

    // Number literal. ADO #404 — hex/binary use Number() (parseFloat returns 0
    // for `0xFF`); `raw` keeps the original spelling for any verbatim codegen.
    if (token.type === TOKEN_TYPES.NUMBER) {
      this.advance();
      return {
        type: AST_TYPES.LITERAL,
        value: /^0[xXbB]/.test(token.value) ? Number(token.value) : parseFloat(token.value),
        raw: token.value,
      };
    }

    // String literal — preserve quote style for ADO #297 (per-arg validation)
    if (token.type === TOKEN_TYPES.STRING) {
      this.advance();
      return {
        type: AST_TYPES.LITERAL,
        value: token.value,
        raw: token.value,
        literalKind: 'string',
        quote: token.quote,
      };
    }

    // ADO #354 — type names: `struct Node`, `union U`, `enum E` (with optional
    // trailing `*`). Lets `sizeof(struct Node)` parse instead of dying on the
    // second identifier ("Expected RPAREN but got IDENTIFIER"). Marked
    // `isType` so extractIdentifiers skips it (a type is not an lvalue/variable
    // and must not surface as an "undeclared variable" error).
    if (token.type === TOKEN_TYPES.IDENTIFIER &&
        ['struct', 'union', 'enum'].includes(token.value) &&
        this.peek(1)?.type === TOKEN_TYPES.IDENTIFIER) {
      this.advance(); // consume struct/union/enum
      const tag = this.advance(); // consume tag name
      let name = `${token.value} ${tag.value}`;
      // Absorb trailing pointer stars (`struct Node *` → `struct Node*`).
      while (this.peek().type === TOKEN_TYPES.OPERATOR && this.peek().value === '*') {
        this.advance();
        name += '*';
      }
      return {
        type: AST_TYPES.IDENTIFIER,
        name,
        isType: true,
      };
    }

    // Identifier (variable or function name)
    if (token.type === TOKEN_TYPES.IDENTIFIER) {
      this.advance();
      return {
        type: AST_TYPES.IDENTIFIER,
        name: token.value,
      };
    }

    // Parenthesized expression
    if (token.type === TOKEN_TYPES.LPAREN) {
      this.advance();
      const expr = this.parseExpression();
      this.expect(TOKEN_TYPES.RPAREN);
      return expr;
    }

    // Handle unexpected tokens
    if (token.type !== TOKEN_TYPES.EOF) {
      this.advance();
      return {
        type: AST_TYPES.IDENTIFIER,
        name: token.value,
        error: true,
      };
    }

    return null;
  }
}

/**
 * Parse an expression string into an AST
 * @param {string} expression - The expression to parse
 * @returns {object} The AST
 */
export const parseExpression = (expression) => {
  if (!expression || typeof expression !== 'string') {
    return { type: AST_TYPES.PROGRAM, body: [], error: 'Empty expression' };
  }

  try {
    const tokenizer = new Tokenizer(expression.trim());
    const tokens = tokenizer.tokenize();
    const parser = new Parser(tokens);
    return parser.parse();
  } catch (error) {
    return {
      type: AST_TYPES.PROGRAM,
      body: [],
      error: error.message,
    };
  }
};

/**
 * Extract all identifiers from an AST
 * @param {object} ast - The AST to analyze
 * @returns {object} - { variables: [], functions: [] }
 */
export const extractIdentifiers = (ast) => {
  const variables = [];
  const functions = [];

  const visit = (node) => {
    if (!node) return;

    if (node.type === AST_TYPES.IDENTIFIER) {
      // Type names (`struct Node` from sizeof/casts) are not variables.
      if (node.isType) return;
      if (!variables.includes(node.name)) {
        variables.push(node.name);
      }
    }

    if (node.type === AST_TYPES.CALL_EXPR) {
      const funcName = getFunctionName(node.callee);
      if (funcName && !functions.includes(funcName)) {
        functions.push(funcName);
      }
      // Visit arguments
      node.arguments?.forEach(visit);
      return; // Don't add callee to variables
    }

    if (node.type === AST_TYPES.MEMBER_EXPR) {
      const memberName = getMemberExpressionName(node);
      // Check if this is being called as a function
      if (memberName && !functions.includes(memberName) && !variables.includes(memberName)) {
        variables.push(memberName);
      }
      // Computed access (`nos[i]`) — visit the index expression so
      // identifiers inside it are captured, AND walk into the object
      // so chained brackets (`nos[i][j]`) yield BOTH indices. Static
      // (`a.b`) member access has property as a bare Identifier
      // already covered by the name builder; don't recurse there.
      if (node.computed) {
        if (node.property) visit(node.property);
        if (node.object) visit(node.object);
      }
    }

    if (node.type === AST_TYPES.BINARY_EXPR) {
      visit(node.left);
      visit(node.right);
    }

    if (node.type === AST_TYPES.UNARY_EXPR) {
      visit(node.argument);
    }

    // ADO #408 — increment/decrement: walk the operand so `p++` / `++arr[i]`
    // extract `p` / `arr` / `i` (and never the operator) as identifiers.
    if (node.type === AST_TYPES.UPDATE_EXPR) {
      visit(node.argument);
    }

    if (node.type === AST_TYPES.CONDITIONAL_EXPR) {
      visit(node.test);
      visit(node.consequent);
      visit(node.alternate);
    }

    if (node.type === AST_TYPES.CAST_EXPR) {
      visit(node.argument);
    }

    if (node.type === AST_TYPES.PROGRAM) {
      node.body?.forEach(visit);
    }
  };

  visit(ast);

  // Remove function names from variables
  const cleanVariables = variables.filter(v => !functions.includes(v));

  return { variables: cleanVariables, functions };
};

/**
 * Get the full name of a function from its callee node
 */
const getFunctionName = (callee) => {
  if (!callee) return null;

  if (callee.type === AST_TYPES.IDENTIFIER) {
    return callee.name;
  }

  if (callee.type === AST_TYPES.MEMBER_EXPR) {
    return getMemberExpressionName(callee);
  }

  return null;
};

/**
 * Get the full name of a member expression (e.g., "Math.abs", "nos").
 * For computed access (`nos[i]`) returns the root object name only —
 * the index identifiers are walked separately by extractIdentifiers.
 */
const getMemberExpressionName = (node) => {
  if (node.type === AST_TYPES.IDENTIFIER) {
    return node.name;
  }

  if (node.type === AST_TYPES.MEMBER_EXPR) {
    const objName = getMemberExpressionName(node.object);
    if (node.computed) {
      // `nos[i]` → just `nos`. We deliberately don't try to render the
      // index inline because it can be an arbitrary expression.
      return objName;
    }
    const propName = node.property?.name || '';
    return `${objName}.${propName}`;
  }

  return '';
};

/**
 * Extract all function calls with their arguments from an AST
 */
export const extractFunctionCalls = (ast) => {
  const calls = [];

  const visit = (node) => {
    if (!node) return;

    if (node.type === AST_TYPES.CALL_EXPR) {
      const funcName = getFunctionName(node.callee);
      calls.push({
        name: funcName,
        arguments: node.arguments || [],
        argCount: (node.arguments || []).length,
      });
      // Also visit arguments for nested calls
      node.arguments?.forEach(visit);
    }

    if (node.type === AST_TYPES.BINARY_EXPR) {
      visit(node.left);
      visit(node.right);
    }

    if (node.type === AST_TYPES.UNARY_EXPR) {
      visit(node.argument);
    }

    if (node.type === AST_TYPES.UPDATE_EXPR) {
      visit(node.argument);
    }

    if (node.type === AST_TYPES.CONDITIONAL_EXPR) {
      visit(node.test);
      visit(node.consequent);
      visit(node.alternate);
    }

    if (node.type === AST_TYPES.CAST_EXPR) {
      visit(node.argument);
    }

    if (node.type === AST_TYPES.PROGRAM) {
      node.body?.forEach(visit);
    }
  };

  visit(ast);
  return calls;
};

/**
 * Extract all operators from an AST
 */
export const extractOperators = (ast) => {
  const operators = [];

  const visit = (node) => {
    if (!node) return;

    if (node.type === AST_TYPES.BINARY_EXPR) {
      if (!operators.includes(node.operator)) {
        operators.push(node.operator);
      }
      visit(node.left);
      visit(node.right);
    }

    if (node.type === AST_TYPES.UNARY_EXPR) {
      if (!operators.includes(node.operator)) {
        operators.push(node.operator);
      }
      visit(node.argument);
    }

    // ADO #408 — capture the `++` / `--` operator and walk the operand.
    if (node.type === AST_TYPES.UPDATE_EXPR) {
      if (!operators.includes(node.operator)) {
        operators.push(node.operator);
      }
      visit(node.argument);
    }

    if (node.type === AST_TYPES.CONDITIONAL_EXPR) {
      if (!operators.includes('?:')) {
        operators.push('?:');
      }
      visit(node.test);
      visit(node.consequent);
      visit(node.alternate);
    }

    if (node.type === AST_TYPES.CAST_EXPR) {
      visit(node.argument);
    }

    if (node.type === AST_TYPES.PROGRAM) {
      node.body?.forEach(visit);
    }
  };

  visit(ast);
  return operators;
};

export default {
  parseExpression,
  extractIdentifiers,
  extractFunctionCalls,
  extractOperators,
  AST_TYPES,
};
